/**
 * useTypingStatus Hook - Real-time Typing Indicators
 *
 * Manages typing status broadcasting and listening via Firestore.
 * Enables real-time "user is typing..." indicators in discussion groups.
 *
 * Features:
 * - Broadcasts current user's typing status to Firestore
 * - Listens for other users' typing status
 * - Filters stale typing indicators (>5 seconds old)
 * - Automatic cleanup on component unmount
 *
 * Firestore Structure:
 * users/{ownerId}/trips/{tripId}/typing_status/{userId}
 * {
 *   userId: string,
 *   userName: string,
 *   isTyping: boolean,
 *   timestamp: serverTimestamp
 * }
 */

import { useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, setDoc, onSnapshot, collection, serverTimestamp, query, where } from 'firebase/firestore';
import { useAuth } from '@trip-planner-monorepo/shared';

/**
 * useTypingStatus Hook
 *
 * @param {string} tripId - Current trip ID
 * @param {string} ownerId - Trip owner ID (for shared trips)
 * @returns {Object} { broadcastTyping, typingUsers }
 */
export const useTypingStatus = (tripId, ownerId = null) => {
  const { currentUser } = useAuth();
  const [typingUsers, setTypingUsers] = useState([]);
  const db = getFirestore();

  // Determine effective user ID (owner for shared trips)
  const effectiveUserId = ownerId || currentUser?.uid;

  /**
   * Broadcast typing status to Firestore
   *
   * @param {boolean} isTyping - Whether user is currently typing
   */
  const broadcastTyping = useCallback(async (isTyping) => {
    if (!currentUser || !tripId || !effectiveUserId) {
      console.warn('[useTypingStatus] Missing required parameters for broadcast');
      return;
    }

    try {
      const typingRef = doc(
        db,
        'users',
        effectiveUserId,
        'trips',
        tripId,
        'typing_status',
        currentUser.uid
      );

      await setDoc(typingRef, {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || 'Anonymous',
        isTyping,
        timestamp: serverTimestamp()
      });

      console.log(`[useTypingStatus] Broadcast typing: ${isTyping}`);
    } catch (error) {
      console.error('[useTypingStatus] Error broadcasting typing status:', error);
    }
  }, [currentUser, tripId, effectiveUserId, db]);

  /**
   * Listen for typing status updates from other users
   */
  useEffect(() => {
    if (!currentUser || !tripId || !effectiveUserId) {
      return;
    }

    console.log('[useTypingStatus] Setting up typing status listener');

    const typingStatusRef = collection(
      db,
      'users',
      effectiveUserId,
      'trips',
      tripId,
      'typing_status'
    );

    // Listen for real-time typing status updates
    const unsubscribe = onSnapshot(
      typingStatusRef,
      (snapshot) => {
        const now = Date.now();
        const activeTypingUsers = [];

        snapshot.forEach((doc) => {
          const data = doc.data();

          // Skip current user
          if (data.userId === currentUser.uid) {
            return;
          }

          // Only include if actively typing
          if (!data.isTyping) {
            return;
          }

          // Filter stale typing indicators (>5 seconds old)
          const typingTimestamp = data.timestamp?.toMillis() || 0;
          const age = now - typingTimestamp;

          if (age < 5000) {
            activeTypingUsers.push({
              userId: data.userId,
              userName: data.userName,
              timestamp: typingTimestamp
            });
          }
        });

        setTypingUsers(activeTypingUsers);
      },
      (error) => {
        console.error('[useTypingStatus] Error listening to typing status:', error);
      }
    );

    // Cleanup on unmount
    return () => {
      console.log('[useTypingStatus] Cleaning up typing status listener');
      unsubscribe();

      // Clear own typing status when component unmounts
      broadcastTyping(false);
    };
  }, [currentUser, tripId, effectiveUserId, db, broadcastTyping]);

  return {
    broadcastTyping,
    typingUsers
  };
};
